import { useQuery } from "@tanstack/react-query";
import api from "../../api/apiClient";
import { NotificationsResponse, NotificationPaginationParams } from "../../types/notification";

/**
 * Hook to fetch admin notifications with pagination
 * - Refetches when window regains focus (browser tab active)
 * - Caches for 30 seconds
 */
export const useAdminNotifications = (params: NotificationPaginationParams & { key?: string }) => {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
  });

  if (params.key) {
    queryParams.append("key", params.key);
  }

  return useQuery({
    queryKey: ["admin-notifications", params.page, params.limit, params.key],
    queryFn: () =>
      api
        .get<NotificationsResponse>(`admin-notification?${queryParams}`)
        .then((res) => res.data),
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true, // Refetch when user focuses the browser tab
    refetchOnMount: true, // Refetch when component mounts
  });
};

/**
 * Hook to fetch unread notification count
 * - Automatically refetches every 60 seconds
 * - Also refetches when window regains focus
 * - This drives the badge count in the notification bell
 */
export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: ["admin-notifications-unread-count"],
    queryFn: () =>
      api
        .get<{ count: number }>(`admin-notification/unread-count`)
        .then((res) => res.data.count),
    refetchInterval: 60000, // Refetch every 60 seconds
    refetchIntervalInBackground: false, // Don't refetch if tab is not focused
    refetchOnWindowFocus: true, // Refetch when user focuses the browser tab
    refetchOnMount: true, // Refetch when component mounts
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });
};
